import React, { useState, useEffect, useRef } from 'react';

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({ value, onChange, placeholder, required }) => {
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const fetchSuggestions = async (query: string) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      // Search in Canada, focusing on Nova Scotia
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query + ', Nova Scotia'
        )}&countrycodes=ca&limit=5&addressdetails=1`,
        {
          headers: {
            'Accept-Language': 'en-CA,en;q=0.9',
          }
        }
      );
      
      if (response.status === 429) {
        console.warn('Address autocomplete rate limited');
        setLoading(false);
        return;
      }

      const data = await response.json();
      
      // Map and filter results
      const nsSuggestions = data.map((item: any) => ({
        display: item.display_name,
        isNS: item.address?.state === 'Nova Scotia' || item.display_name.toLowerCase().includes('nova scotia')
      })).filter((item: any) => item.isNS);
      
      setSuggestions(nsSuggestions);
      setShowSuggestions(nsSuggestions.length > 0);
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    // Debounce fetch (400ms)
    if (newValue.length >= 3) {
      timeoutRef.current = setTimeout(() => fetchSuggestions(newValue), 400);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelect = (display: string) => {
    onChange(display);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        style={{ 
          width: '100%',
          padding: '14px',
          borderRadius: '12px',
          border: '1px solid #ddd',
          fontSize: '1rem',
          boxSizing: 'border-box'
        }}
      />
      {loading && (
        <div style={{ 
          position: 'absolute', 
          right: '12px', 
          top: '50%', 
          transform: 'translateY(-50%)', 
          fontSize: '0.75rem', 
          color: 'var(--primary-color)',
          fontWeight: 'bold'
        }}>
          Searching...
        </div>
      )}
      {showSuggestions && suggestions.length > 0 && (
        <ul style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
          listStyle: 'none',
          padding: '8px 0',
          margin: '8px 0 0 0',
          zIndex: 9999,
          maxHeight: '250px',
          overflowY: 'auto',
          textAlign: 'left'
        }}>
          {suggestions.map((item, index) => (
            <li
              key={index}
              onClick={() => handleSelect(item.display)}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                color: '#333',
                lineHeight: '1.4',
                transition: 'background 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f0f7ff'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff'}
            >
              {item.display}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;
